import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { communityTrpc } from '@org/clients/community';
import { Skeleton } from '@org/ui';
import { Calendar, Video } from 'lucide-react';

export const Route = createFileRoute('/events')({
  component: EventsPage,
});

const URL_RE = /(https?:\/\/\S+|meet\.google\.com\/\S+)/i;

function extractJoinUrl(location: string | null): string | null {
  const match = location?.match(URL_RE)?.[0];
  if (!match) return null;
  return match.startsWith('http') ? match : `https://${match}`;
}

function EventsPage() {
  const { t, i18n } = useTranslation('community');
  const { data, isLoading } = communityTrpc.events.upcoming.useQuery(
    { limit: 20 },
    { staleTime: 60_000 }
  );

  // Open-audience events only — group/private classes also live in the events
  // table but are not community content.
  const events = (data ?? []).filter((event) => event.eventKind === 'open-event');

  const dateFmt = new Intl.DateTimeFormat(i18n.language, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
  const timeFmt = new Intl.DateTimeFormat(i18n.language, {
    hour: 'numeric',
    minute: 'numeric',
  });
  const monthFmt = new Intl.DateTimeFormat(i18n.language, { month: 'short' });

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t('events.title')}</h1>
        <p className="text-muted-foreground">{t('events.subtitle')}</p>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-start gap-4 rounded-2xl border border-border bg-card p-4">
              <Skeleton className="h-12 w-12 shrink-0 rounded-xl" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-3 w-1/3" />
              </div>
            </div>
          ))}
        </div>
      ) : events.length === 0 ? (
        <div className="rounded-2xl border border-border bg-card p-8 text-center">
          <Calendar className="mx-auto h-12 w-12 text-muted-foreground" />
          <p className="mt-4 text-muted-foreground">{t('events.empty')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {events.map((event) => {
            const start = new Date(event.startsAt);
            const end = event.endsAt ? new Date(event.endsAt) : null;
            const joinUrl = extractJoinUrl(event.location);
            return (
              <div
                key={event.id}
                className="flex flex-wrap items-start gap-4 rounded-2xl border border-border bg-card p-4"
              >
                <div className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-xl bg-brand/10 text-brand">
                  <span className="text-xs font-medium uppercase leading-none">
                    {monthFmt.format(start)}
                  </span>
                  <span className="text-base font-bold leading-tight">{start.getDate()}</span>
                </div>
                <div className="min-w-[12rem] flex-1">
                  <p className="font-semibold text-foreground">{event.title}</p>
                  <p className="text-sm text-muted-foreground">
                    {event.category && <span>{event.category} · </span>}
                    {dateFmt.format(start)} · {timeFmt.format(start)}
                    {end && ` – ${timeFmt.format(end)}`}
                  </p>
                </div>
                {joinUrl && (
                  <a
                    href={joinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-auto inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-brand px-3.5 py-2 text-xs font-semibold text-white transition-colors hover:bg-brand/90"
                  >
                    <Video className="h-4 w-4" />
                    {t('events.join')}
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
